import { digitsOnly, normalizeCountryCode, normalizeNationalNumber } from "./phone";
import { normalizeSearchText } from "./searchText";

export function normalizeMerchantRoleKey(role) {
  return String(role || "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLocaleLowerCase("tr-TR");
}

export function isWarehouseResponsibleRole(role) {
  const normalizedRole = normalizeMerchantRoleKey(role);
  return normalizedRole === "warehouseresp" || normalizedRole === "warehouseresponsible" || normalizedRole === "deposorumlusu";
}

function uniqIds(values) {
  return Array.from(new Set((Array.isArray(values) ? values : []).map((value) => String(value ?? "").trim()).filter(Boolean)));
}

function getUserIdCandidates(user, fallbackUserID = "") {
  if (!user || typeof user !== "object") return uniqIds([fallbackUserID]);
  return uniqIds([
    fallbackUserID,
    user.userID,
    user.userId,
    user.user_id,
    user.id,
  ]);
}

function getWarehouseResponsibleIdCandidates(warehouse) {
  if (!warehouse || typeof warehouse !== "object") return [];
  const responsible = warehouse.responsible && typeof warehouse.responsible === "object" ? warehouse.responsible : null;
  const responsibleUser = warehouse.responsibleUser && typeof warehouse.responsibleUser === "object" ? warehouse.responsibleUser : null;
  const warehouseResponsible = warehouse.warehouseResponsible && typeof warehouse.warehouseResponsible === "object" ? warehouse.warehouseResponsible : null;

  return uniqIds([
    warehouse.responsibleUserID,
    warehouse.responsibleUserId,
    warehouse.responsible_user_id,
    warehouse.responsibleID,
    warehouse.responsibleId,
    warehouse.responsible_id,
    warehouse.warehouseResponsibleUserID,
    warehouse.warehouseResponsibleUserId,
    warehouse.warehouse_responsible_user_id,
    warehouse.warehouseResponsibleID,
    warehouse.warehouseResponsibleId,
    warehouse.warehouse_responsible_id,
    responsible?.userID,
    responsible?.userId,
    responsible?.user_id,
    responsible?.id,
    responsibleUser?.userID,
    responsibleUser?.userId,
    responsibleUser?.user_id,
    responsibleUser?.id,
    warehouseResponsible?.userID,
    warehouseResponsible?.userId,
    warehouseResponsible?.user_id,
    warehouseResponsible?.id,
  ]);
}

function normalizeName(value) {
  return normalizeSearchText(String(value || ""), { compact: true });
}

function getUserFullName(user) {
  if (!user || typeof user !== "object") return "";
  return [user.name, user.lastname]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");
}

function getWarehouseResponsibleFullName(warehouse) {
  if (!warehouse || typeof warehouse !== "object") return "";
  const responsible = warehouse.responsible && typeof warehouse.responsible === "object" ? warehouse.responsible : null;
  const responsibleUser = warehouse.responsibleUser && typeof warehouse.responsibleUser === "object" ? warehouse.responsibleUser : null;
  const warehouseResponsible = warehouse.warehouseResponsible && typeof warehouse.warehouseResponsible === "object" ? warehouse.warehouseResponsible : null;

  const composedName = [warehouse.responsibleName, warehouse.responsibleLastname]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

  return (
    composedName ||
    warehouse.responsiblePerson ||
    warehouse.responsibleFullName ||
    warehouse.responsible_full_name ||
    getUserFullName(responsible) ||
    getUserFullName(responsibleUser) ||
    getUserFullName(warehouseResponsible)
  );
}

function getPhoneCandidates(phone, countryCode) {
  const rawPhone = String(phone || "").trim();
  if (!rawPhone) return [];

  const normalizedCountryCode = normalizeCountryCode(countryCode || "");
  const nationalNumber = normalizeNationalNumber(rawPhone, normalizedCountryCode || "90");
  const rawDigits = digitsOnly(rawPhone);

  return uniqIds([
    rawDigits,
    nationalNumber,
    normalizedCountryCode && nationalNumber ? `${normalizedCountryCode}${nationalNumber}` : "",
  ]);
}

function getUserPhoneCandidates(user) {
  if (!user || typeof user !== "object") return [];
  return getPhoneCandidates(
    user.phone,
    user.countryCode ?? user.country_code
  );
}

function getWarehouseResponsiblePhoneCandidates(warehouse) {
  if (!warehouse || typeof warehouse !== "object") return [];
  const responsible = warehouse.responsible && typeof warehouse.responsible === "object" ? warehouse.responsible : null;
  const responsibleUser = warehouse.responsibleUser && typeof warehouse.responsibleUser === "object" ? warehouse.responsibleUser : null;
  const warehouseResponsible = warehouse.warehouseResponsible && typeof warehouse.warehouseResponsible === "object" ? warehouse.warehouseResponsible : null;

  return uniqIds([
    ...getPhoneCandidates(warehouse.responsiblePhone, warehouse.countryCode ?? warehouse.country_code),
    ...getPhoneCandidates(warehouse.responsible_phone, warehouse.countryCode ?? warehouse.country_code),
    ...getPhoneCandidates(responsible?.phone, responsible?.countryCode ?? responsible?.country_code),
    ...getPhoneCandidates(responsibleUser?.phone, responsibleUser?.countryCode ?? responsibleUser?.country_code),
    ...getPhoneCandidates(warehouseResponsible?.phone, warehouseResponsible?.countryCode ?? warehouseResponsible?.country_code),
  ]);
}

function idsIntersect(left, right) {
  const leftSet = new Set(uniqIds(left));
  return uniqIds(right).some((id) => leftSet.has(id));
}

export function isWarehouseResponsibleForWarehouse(warehouse, userInfo, fallbackUserID = "") {
  if (!warehouse || !userInfo || typeof warehouse !== "object" || typeof userInfo !== "object") return false;

  const warehouseResponsibleIds = getWarehouseResponsibleIdCandidates(warehouse);
  if (warehouseResponsibleIds.length > 0 && idsIntersect(warehouseResponsibleIds, getUserIdCandidates(userInfo, fallbackUserID))) {
    return true;
  }

  const userPhones = getUserPhoneCandidates(userInfo);
  const warehousePhones = getWarehouseResponsiblePhoneCandidates(warehouse);
  if (userPhones.length > 0 && warehousePhones.length > 0) {
    return idsIntersect(userPhones, warehousePhones);
  }

  const userName = normalizeName(getUserFullName(userInfo));
  const warehouseResponsibleName = normalizeName(getWarehouseResponsibleFullName(warehouse));
  return Boolean(userName && warehouseResponsibleName && userName === warehouseResponsibleName);
}
